import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import { useEffect, useRef, useState } from 'react';
import { useStack } from '../../../../../../hooks/useStack';
import { patchCatalogKitOrderQuantity } from '../../../../../../services/modules/catalogProducts/catalogKitQuantityCache.utils';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import useOrderBook from '../../../../../order-book/hooks/useOrderBook';
import { ordersRepository } from '../../../../../orders/repositories/ordersRepository/orders.repository';
import { invalidateAffectedOrderProductsGridQueries } from '../../../../../orders/utils/orderCalculate.utils';
import useHandleUpdateKitOnOrderErrorResponse from '../../../../../products/products-details-v2/components/KitDetailsList/hooks/useHandleUpdateKitErrorResponse';
import { StackKit } from '../../../../../products/products-details-v2/components/KitDetailsList/types/kitDetailsList.types';
import { kitActionsRepository } from '../../../../../products/repositories/kitActions/kitActions.repository';

interface UseCatalogKitQuantityParams {
  kitId: string;
  orderId?: string;
  payloadQuantity: number;
  availableAmount: number | null;
  isPublicAccess: boolean;
}

const useCatalogKitQuantity = ({
  kitId,
  orderId,
  payloadQuantity,
  availableAmount,
  isPublicAccess,
}: UseCatalogKitQuantityParams) => {
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const { openOrderBook } = useOrderBook();
  const { handleError } = useHandleUpdateKitOnOrderErrorResponse();
  const selectedOrderId = useOrderBookStore((state) => state.selectedOrder?.id);
  const reloadOrderItemsQuantity = useOrderBookStore((state) => state.reloadOrderItemsQuantity);

  const inputRef = useRef<HTMLInputElement>(null);
  const [quantity, setQuantity] = useState(payloadQuantity);
  const [isRemoving, setIsRemoving] = useState(false);

  const resetInputValue = (value: number) => {
    const nextValue = value ? String(value) : '';

    if (!inputRef.current || inputRef.current.value === nextValue) {
      return;
    }

    inputRef.current.value = nextValue;
  };

  const refreshAfterKitChange = async (targetOrderId: string) => {
    try {
      const affectedOrderProducts = await reloadOrderItemsQuantity(
        true,
        targetOrderId,
        targetOrderId === selectedOrderId,
      );

      invalidateAffectedOrderProductsGridQueries({
        orderId: targetOrderId,
        queryClient,
        teamId: team?.id,
        isOfflineMode: false,
        affectedOrderProducts: affectedOrderProducts ?? [],
      });
    } catch {
      return;
    }
  };

  const persistKit = async (kits: StackKit[]) => {
    const [kitToPersist] = kits;

    if (!kitToPersist) {
      return;
    }

    const response = await ordersRepository(kitToPersist.orderId).createOrUpdateOrderListWithKit(
      kitToPersist,
    );

    if (!response.ok) {
      handleError(response.data);
      setQuantity(payloadQuantity);
      resetInputValue(payloadQuantity);
      return;
    }

    patchCatalogKitOrderQuantity({
      queryClient,
      orderId: kitToPersist.orderId,
      kitId: kitToPersist.kitId,
      orderQuantity: kitToPersist.quantity,
    });

    await refreshAfterKitChange(kitToPersist.orderId);
  };

  const { pushItem, stackRef } = useStack<StackKit>({
    getUniqueKey: (item) => `${item.kitId}-${item.orderId}`,
    onFinish: persistKit,
  });

  useEffect(() => {
    if (stackRef.current.length > 0) {
      return;
    }

    setQuantity(payloadQuantity);
    resetInputValue(payloadQuantity);
  }, [payloadQuantity]);

  const onRemove = async () => {
    if (isPublicAccess) {
      return;
    }

    if (!orderId) {
      openOrderBook();
      return;
    }

    stackRef.current = [];

    setIsRemoving(true);
    const response = await kitActionsRepository().unshopKit({ orderId, kitId });
    setIsRemoving(false);

    if (!response.ok) {
      handleError(response.data);
      return;
    }

    setQuantity(0);
    resetInputValue(0);
    patchCatalogKitOrderQuantity({ queryClient, orderId, kitId, orderQuantity: 0 });

    await refreshAfterKitChange(orderId);
  };

  const onChangeQuantity = (value: number) => {
    const isSameQuantity = value === quantity;
    const isOverStock = !R.isNil(availableAmount) && value > availableAmount;

    if (isSameQuantity || isOverStock) {
      resetInputValue(quantity);
      return;
    }

    if (!orderId) {
      resetInputValue(quantity);
      openOrderBook();
      return;
    }

    if (value === 0) {
      void onRemove();
      return;
    }

    setQuantity(value);
    resetInputValue(value);
    pushItem({ kitId, orderId, quantity: value });
  };

  return { quantity, isRemoving, inputRef, onChangeQuantity, onRemove };
};

export default useCatalogKitQuantity;
